import { ServerEnv } from 'Configs/ServerEnv'
import storeCountry from 'GlobalStores/Cores/County/StoreCountry'
import httpHelper from 'Services/HttpHelper'
import { makeAutoObservable, runInAction } from 'mobx'

const PRESERVED_LOCAL_STORAGE_KEYS = ['currentLanguage']
const PRESERVED_SESSION_STORAGE_KEYS = []

class sessionAuth {
    storeKey = 'login-extends'

    // Per-tab identity (sessionStorage is NOT shared across tabs, unlike the token in localStorage).
    // Snapshot of the baId that owns THIS tab's session, used to detect when another tab
    // logs in and overrides the shared `login-extends` token.
    tabIdKey = 'tab-ba-id'

    user = null
    users = []

    needResetPassword = false

    SetNeedResetPassword(value) {
        this.needResetPassword = value
    }

    NeedResetPassword() {
        return this.needResetPassword
    }

    ResetSessionStorage(removeCookie = true) {
        Object.keys(sessionStorage)
            .filter(key => !PRESERVED_SESSION_STORAGE_KEYS.includes(key))
            .forEach(key => sessionStorage.removeItem(key))
        Object.keys(localStorage)
            .filter(key => !PRESERVED_LOCAL_STORAGE_KEYS.includes(key))
            .forEach(key => localStorage.removeItem(key))

        if (removeCookie) {
            // Clear all cookies
            const cookies = document.cookie.split(';')
            const host = window.location.hostname
            const hostParts = host.split('.')
            const domain = hostParts.length >= 2 ? '.' + hostParts.slice(-2).join('.') : null

            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i]
                const eqPos = cookie.indexOf('=')
                const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim()
                const expires = 'expires=Thu, 01 Jan 1970 00:00:00 GMT'
                // Try all domain/path combinations to ensure stubborn cookies are deleted
                document.cookie = `${name}=;${expires};path=/`
                document.cookie = `${name}=;${expires};path=/;domain=${host}`
                if (domain) {
                    document.cookie = `${name}=;${expires};path=/;domain=${domain}`
                }
            }

            // Clear IndexedDB
            if (window.indexedDB?.databases) {
                window.indexedDB.databases().then(dbs => {
                    dbs.forEach(db => window.indexedDB.deleteDatabase(db.name))
                })
            }

            // Clear Cache API (service worker caches)
            if (window.caches) {
                window.caches.keys().then(keys => {
                    keys.forEach(key => window.caches.delete(key))
                })
            }
        }

    }

    async Current() {
        await this.GetStoredSession()
            .then(response => {
                runInAction(() => {
                    this.user = response
                })
            })
            .catch(error => {
                console.log('error', error)
            })

        return this.user
    }

    Token() {
        return this.GetLoginExtend('token')
    }

    Href() {
        return this.GetLoginExtend('href')
    }

    CustomerData() {
        let customerData = sessionStorage.getItem('customerData')
        if (customerData) {
            return JSON.parse(customerData)
        } else {
            return null
        }
    }

    SaveSession(data) {
        delete data.customerData
        let loginExtends = this.GetLoginExtends()
        loginExtends[storeCountry.CountryLowerCase()] = data
        this.SaveLoginExtends(JSON.stringify(loginExtends))

        // Stamp this tab as the owner of the session we just wrote.
        this.MarkTabIdentity(data.id)

        runInAction(() => {
            this.user = data
        })
    }

    // ─── Cross-tab account-hijack detection ──────────────────────────────
    // The token lives in localStorage (shared across tabs), so a login in tab B
    // overrides tab A's token. These helpers let tab A notice that the token it
    // now holds no longer belongs to the account it originally logged in as.

    MarkTabIdentity(baId) {
        if (baId === undefined || baId === null) {
            return
        }
        sessionStorage.setItem(this.tabIdKey, String(baId))
    }

    GetTabIdentity() {
        return sessionStorage.getItem(this.tabIdKey)
    }

    // baId currently stored in the shared localStorage token for this country.
    CurrentStoredId() {
        const stored = this.GetLoginExtends()[storeCountry.CountryLowerCase()]
        return stored && stored.id !== undefined && stored.id !== null ? String(stored.id) : null
    }

    // True when the shared token still belongs to the account that owns THIS tab.
    // Returns true when there is nothing to compare (no tab identity yet, or no
    // stored session) so guest/restore flows are never blocked.
    IsTokenOwnedByThisTab() {
        const tabId = this.GetTabIdentity()
        const storedId = this.CurrentStoredId()

        if (!tabId || !storedId) {
            return true
        }

        return tabId === storedId
    }

    // Realtime detection (Method 1). Fires the callback when another tab overrides
    // the shared token with a different account. Returns an unsubscribe function.
    SubscribeAccountConflict(callback) {
        const handler = event => {
            // Only react to changes of the shared token key, or a full clear (key === null).
            if (event.key !== null && event.key !== this.storeKey) {
                return
            }
            if (!this.GetTabIdentity()) {
                return
            }
            if (!this.IsTokenOwnedByThisTab()) {
                callback()
            }
        }

        window.addEventListener('storage', handler)
        return () => window.removeEventListener('storage', handler)
    }

    GetLoginExtends() {
        const defaultObjects = {
            [storeCountry.CountryLowerCase()]: null
        }

        return localStorage.getItem(this.storeKey) ? JSON.parse(localStorage.getItem(this.storeKey)) : defaultObjects
    }

    GetLoginExtend(key) {
        const loginExtends = this.GetLoginExtends()

        return loginExtends[storeCountry.CountryLowerCase()] ? loginExtends[storeCountry.CountryLowerCase()][key] || null : null
    }

    SaveLoginExtends(data) {
        localStorage.setItem(this.storeKey, data)
    }

    SetLoginExtend(key, value) {
        const loginExtends = this.GetLoginExtends()
        if (loginExtends[storeCountry.CountryLowerCase()]) {
            loginExtends[storeCountry.CountryLowerCase()][key] = value
        }

        this.SaveLoginExtends(JSON.stringify(loginExtends))
    }

    async CheckToken(token, country, baId) {

        let url = `https://${ServerEnv.MemberCalls2()}.unicity.com/etlV2/refreshToken?ushopCountryCode=${country}&baId=${baId}&login_id=${baId}`

        if (ServerEnv.IsDev()) {
            url += `&isQa=1`
        }

        const response = await httpHelper.Get({
            url,
            config: {
                headers: {
                    'authorization-hydra': `Bearer ${token}`
                }
            }
        })

        return response
    }

    ShouldRestore() {
        console.log('ShouldRestore')
        return new Promise((resolve, reject) => {
            if (this.GetLoginExtends()[storeCountry.CountryLowerCase()]) {
                const hasToken = this.GetLoginExtend('token')
                if (hasToken) {
                    resolve(true)
                }
            } else {
                this.ResetSessionStorage()
                reject({
                    message: 'no-token'
                })
            }
        })
    }

    FetchAll() {
        this.users = []
        return new Promise((resolve, reject) => {
            let loginExtends = localStorage.getItem(this.storeKey)
            if (loginExtends) {
                loginExtends = JSON.parse(loginExtends)
                if (loginExtends[storeCountry.CountryLowerCase()] && loginExtends.length > 0) {
                    loginExtends[storeCountry.CountryLowerCase()].map((v, k) => {
                        this.users.push(v)
                    })
                } else {
                    reject(this.users)
                }
            }
        })
    }

    GetStoredSession() {
        return new Promise((resolve, reject) => {
            let loginExtends = localStorage.getItem(this.storeKey)
            if (loginExtends) {
                loginExtends = JSON.parse(loginExtends)
                resolve(loginExtends[storeCountry.CountryLowerCase()])
            }

            reject(null)
        })
    }

    RemoveSession(force = false) {
        if (this.user || force === true) {
            localStorage.removeItem('last-visited')
            const clearData = resolve => {
                let stored = this.GetLoginExtends()
                if (stored[storeCountry.CountryLowerCase()]) {
                    delete stored[storeCountry.CountryLowerCase()]
                    this.SaveLoginExtends(JSON.stringify(stored))
                }

                this.ResetSessionStorage()
                runInAction(() => {
                    this.user = null
                })
                if (resolve) {
                    resolve()
                }
            }

            if (force === false) {
                clearData()
            } else {
                return new Promise((resolve, reject) => {
                    clearData(resolve)
                })
            }
        }
    }

    constructor() {
        makeAutoObservable(this)
    }
}
const SessionAuth = new sessionAuth()
export default SessionAuth
